import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from '@mui/material'
import Box from '@mui/material/Box'

import {
  ApiLogEntry,
  ApiLogQuery,
  refreshApiLog,
  useGetApiLog,
} from 'app/hooks/activity/useGetApiLog'
import {useOnLoadGet} from 'app/hooks/common/useOnLoadGet'
import {CTenant, CUser} from 'app/modules/discovery/core'
import {DateTime} from 'luxon'
import {useEffect, useMemo, useState} from 'react'
import AdminWrapper from './AdminWrapper'
interface confirmationProps {
  title: string | null
  message: string | null
  action: () => void
}

type HttpAction = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'na'

interface ILogEntry {
  id: string
  action: 'GET' | 'POST' | 'PUT' | 'DELETE'
  route: string
  body: string | null
  status_code: number | null
  error: {
    class: string
    message: string
    display_message: string | null
    trace: string
  } | null
  user: CUser | null
  tenant: CTenant | null
  request_timestamp: DateTime
  response_timestamp: DateTime | null
  api_version: string
  api_env: string
  duration: number | null
  styling: {
    backgroundColor: string
  }
  formattedBody: string | null
}

function ActivityLogPage() {
  const [excludeSelfFilter, setExcludeSelfFilter] = useState(true)
  const [methodFilter, setMethodFilter] = useState<HttpAction>('na')
  const [statusCodeFilter, setStatusCodeFilter] = useState<number | null>(null)

  const apiQuery: ApiLogQuery = {}
  if (excludeSelfFilter) {
    apiQuery.route__excl = '/api/admin/api_log'
  }
  if (methodFilter !== 'na') {
    apiQuery.method = methodFilter
  }
  if (statusCodeFilter !== null) {
    apiQuery.status_code = statusCodeFilter
  }

  const apiLogs = useGetApiLog(apiQuery)
  const users = useOnLoadGet<any>(`std/user`)
  const tenants = useOnLoadGet<any>(`std/tenant`)

  function entryBkgd(entry: ApiLogEntry) {
    if (entry.status_code !== null && entry.status_code >= 200 && entry.status_code <= 299) {
      return '#e0ffe0'
    }
    if (entry.status_code !== null && entry.status_code >= 400 && entry.status_code <= 599) {
      return '#ffe0e0'
    }
    return '#ffffe0'
  }

  const dependency = [excludeSelfFilter, methodFilter, statusCodeFilter]

  const userLookup = useMemo(() => {
    const output: Record<string, CUser> = {}
    if (users?.data) {
      for (const user of users.data) {
        if (user.id !== undefined) {
          output[user.id] = user
        }
      }
    }
    return output
  }, [users])

  const tenantLookup = useMemo(() => {
    const output: Record<string, CTenant> = {}
    if (tenants?.data) {
      for (const tenant of tenants.data) {
        if (tenant.id !== undefined) {
          output[tenant.id] = tenant
        }
      }
    }
    return output
  }, [])

  class LogEntry implements ILogEntry {
    id
    action
    route
    body
    status_code
    error
    request_timestamp
    response_timestamp
    api_version
    api_env

    _user_id: string | null
    _tenant_id: string | null

    constructor(data: ApiLogEntry) {
      this.id = data.id
      this.action = data.method as 'GET' | 'POST' | 'PUT' | 'DELETE'
      this.route = data.route
      this.body = data.body
      this.status_code = data.status_code
      this.error = null
      if (data.error_class && data.error_message && data.error_trace) {
        this.error = {
          class: data.error_class,
          message: data.error_message,
          display_message: data.error_display_message,
          trace: data.error_trace,
        }
      }
      this.request_timestamp = DateTime.fromISO(data.request_timestamp)
      this.response_timestamp = data.response_timestamp
        ? DateTime.fromISO(data.response_timestamp)
        : null
      this.api_version = data.api_version
      this.api_env = data.api_env
      this._user_id = data.user_id
      this._tenant_id = data.tenant_id
    }

    get user() {
      if (this._user_id && userLookup[this._user_id]) {
        return userLookup[this._user_id]
      }
      return null
    }

    get tenant() {
      if (this._tenant_id && tenantLookup[this._tenant_id]) {
        return tenantLookup[this._tenant_id]
      }
      return null
    }

    get styling() {
      const output = {backgroundColor: '#ffffe0'}

      if (this.status_code !== null && this.status_code >= 200 && this.status_code <= 299) {
        output.backgroundColor = '#e0ffe0'
      }
      if (this.status_code !== null && this.status_code >= 400 && this.status_code <= 599) {
        output.backgroundColor = '#ffe0e0'
      }
      return output
    }

    _userName(verbose = false) {
      if (this.user === null) {
        return '<none>'
      }
      if (this.user.first_name && this.user.last_name) {
        if (verbose) {
          return `${this.user.last_name}, ${this.user.first_name}`
        }
        return `${this.user.last_name}, ${this.user.first_name[0]}.`
      }
      return `<${this.user.email}>`
    }
    get userLongName() {
      return this._userName(true)
    }
    get userShortName() {
      return this._userName(false)
    }

    get displayRequestTimestamp() {
      return this.request_timestamp.toFormat('yyyy LLL dd, hh:MM:ss a')
    }

    get displayResponseTimestamp() {
      if (!this.response_timestamp) {
        return ''
      }
      return this.response_timestamp.toFormat('yyyy LLL dd, hh:MM:ss a')
    }

    get duration() {
      if (!this.response_timestamp) {
        return null
      }
      return this.response_timestamp.diff(this.request_timestamp, 'seconds').seconds
    }

    get formattedBody() {
      if (!this.body) {
        return null
      }
      if (this.body.length < 100) {
        return this.body
      }
      try {
        return JSON.stringify(JSON.parse(this.body), null, 2)
      } catch (err) {
        return this.body
      }
    }
  }

  const entries = useMemo(
    () => apiLogs.data?.map((x) => new LogEntry(x)) ?? [],
    [!!apiLogs.data, !!users.data, !!tenants.data]
  )

  useEffect(() => {
    refreshApiLog(apiQuery)
  }, dependency)

  return (
    <AdminWrapper>
      <Box>
        <Box>
          <Box sx={{minHeight: 'calc(100vh - 72px)'}}>
            <Box
              sx={{
                padding: '1em',
                backgroundColor: 'white',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flex: '0 0 auto',
                  maxWidth: '1024px',
                  marginLeft: 'calc(max(50vw - 632px, 0px))',
                }}
              >
                <Box sx={{flex: '1 1 auto'}}></Box>
                <FormControlLabel
                  sx={{flex: '0 0 auto', marginLeft: '12px'}}
                  label='Exclude this page'
                  control={
                    <Switch
                      checked={excludeSelfFilter}
                      onChange={() => {
                        setExcludeSelfFilter(!excludeSelfFilter)
                      }}
                    />
                  }
                ></FormControlLabel>
                <FormControl variant={'standard'} sx={{m: 1, minWidth: 150, marginLeft: '12px'}}>
                  <InputLabel id='method-label'>HTTP Action</InputLabel>
                  <Select
                    id='method-select'
                    labelId='method-label'
                    value={methodFilter}
                    onChange={(event) => {
                      if (
                        event.target.value === 'GET' ||
                        event.target.value === 'POST' ||
                        event.target.value === 'PUT' ||
                        event.target.value === 'DELETE'
                      ) {
                        setMethodFilter(event.target.value)
                      } else {
                        setMethodFilter('na')
                      }
                    }}
                  >
                    <MenuItem value='na'>(all actions)</MenuItem>
                    <MenuItem value='GET'>GET</MenuItem>
                    <MenuItem value='POST'>POST</MenuItem>
                    <MenuItem value='PUT'>PUT</MenuItem>
                    <MenuItem value='DELETE'>DELETE</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box
              sx={{
                maxWidth: '1024px',
                marginLeft: 'calc(max(50vw - 632px, 0px))',
              }}
            >
              <Box sx={{margin: '10px'}}>
                <Box>
                  <Box></Box>
                </Box>
                {entries.map((entry) => (
                  <Accordion key={entry.id} elevation={0} sx={entry.styling}>
                    <AccordionSummary
                      sx={{minHeight: 'unset', '& .MuiAccordionSummary-content': {margin: '2px 0'}}}
                      expandIcon={<ExpandMoreIcon></ExpandMoreIcon>}
                    >
                      <Box sx={{display: 'flex', width: '100%'}}>
                        <Box sx={{width: '180px', flex: '0 0 auto'}}>
                          {entry.displayRequestTimestamp}
                        </Box>
                        <Box sx={{width: '50px', flex: '0 0 auto'}}>{entry.status_code}</Box>
                        <Box sx={{width: '65px', flex: '0 0 auto', fontWeight: 'bold'}}>
                          {entry.action}
                        </Box>
                        <Box sx={{flex: '1 1 auto', fontWeight: 'bold'}}>{entry.route}</Box>
                        <Box sx={{width: '160px', flex: '0 0 auto'}}>{entry.userShortName}</Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: 'flex',
                          borderTop: '1px solid #888',
                          borderBottom: '1px solid #888',
                          padding: '6px 0',
                        }}
                      >
                        <Box sx={{marginRight: '24px', flex: '0 0 auto', minWidth: '200px'}}>
                          <Box sx={{fontSize: 'smaller', fontStyle: 'bold', color: '#444'}}>
                            user
                          </Box>
                          <Box>{entry.userLongName}</Box>
                        </Box>
                        <Box sx={{marginRight: '24px', flex: '0 0 auto', minWidth: '160px'}}>
                          <Box sx={{fontSize: 'smaller', fontStyle: 'bold', color: '#444'}}>
                            tenant
                          </Box>
                          <Box>{entry.tenant?.name ?? '<none>'}</Box>
                        </Box>
                        {entry.duration ? (
                          <Box sx={{marginRight: '24px', flex: '1 1 auto', minWidth: '100px'}}>
                            <Box sx={{fontSize: 'smaller', fontStyle: 'bold', color: '#444'}}>
                              duration
                            </Box>
                            <Box>{entry.duration} seconds</Box>
                          </Box>
                        ) : (
                          <></>
                        )}
                        <Box sx={{marginRight: '24px', flex: '0 0 auto', minWidth: '80px'}}>
                          <Box sx={{fontSize: 'smaller', fontStyle: 'bold', color: '#444'}}>
                            api env
                          </Box>
                          <Box>{entry.api_env}</Box>
                        </Box>
                        <Box sx={{flex: '0 0 auto', minWidth: '80px'}}>
                          <Box sx={{fontSize: 'smaller', fontStyle: 'bold', color: '#444'}}>
                            api version
                          </Box>
                          <Box>{entry.api_version}</Box>
                        </Box>
                      </Box>
                      {entry.body ? (
                        <Box
                          sx={{
                            border: '1px solid #800000',
                            margin: '30px 6px 6px',
                            padding: '12px 24px',
                          }}
                        >
                          <Box sx={{textAlign: 'center'}}>
                            <Box sx={{fontWeight: 'bold'}}>BODY</Box>
                          </Box>
                          <Box>
                            <pre style={{whiteSpace: 'pre-wrap'}}>{entry.formattedBody}</pre>
                          </Box>
                        </Box>
                      ) : (
                        <></>
                      )}
                      {entry.error ? (
                        <Box
                          sx={{
                            border: '1px solid #800000',
                            margin: '30px 6px 6px',
                            padding: '12px',
                          }}
                        >
                          <Box sx={{textAlign: 'center'}}>
                            <Box sx={{fontWeight: 'bold'}}>ERROR: {entry.error.class}</Box>
                          </Box>
                          <Box>
                            <Box sx={{fontWeight: 'bold'}}>Message:</Box>
                            <Box sx={{margin: '0 12px;'}}>{entry.error.message}</Box>
                          </Box>
                          {entry.error.display_message ? (
                            <Box>
                              <Box sx={{fontWeight: 'bold'}}>Display Message:</Box>
                              <Box sx={{margin: '0 12px;'}}>{entry.error.display_message}</Box>
                            </Box>
                          ) : (
                            <></>
                          )}
                          <Box>
                            <Box sx={{fontWeight: 'bold'}}>Stack Trace:</Box>
                            <Box sx={{margin: '0 12px;'}}>
                              <pre style={{whiteSpace: 'pre-wrap'}}>{entry.error.trace}</pre>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <></>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </AdminWrapper>
  )
}

export {ActivityLogPage}
